import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { PortalAuthService } from './portal-auth.service';

// The portal's only data source. Every call carries the caller's Firebase ID
// token; the server derives what they may see from it. Nothing here takes a
// customerId that widens access — `customerId` only ever narrows the view to
// one of the customers the account is already linked to.
@Injectable({ providedIn: 'root' })
export class PortalApiService {

  private base = environment.portalApi;

  constructor(private http: HttpClient, private auth: PortalAuthService) { }

  private async options(params?: { [k: string]: any }) {
    const token = await this.auth.getToken();
    let httpParams = new HttpParams();
    Object.keys(params || {}).forEach(k => {
      const v = (params as any)[k];
      if (v !== null && v !== undefined && v !== '') { httpParams = httpParams.set(k, String(v)); }
    });
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + (token || '')
      }),
      params: httpParams
    };
  }

  private async get<T>(path: string, params?: any): Promise<T> {
    return this.http.get<T>(this.base + path, await this.options(params)).toPromise() as Promise<T>;
  }

  private async post<T>(path: string, body: any): Promise<T> {
    return this.http.post<T>(this.base + path, JSON.stringify(body || {}), await this.options()).toPromise() as Promise<T>;
  }

  // ---- session -------------------------------------------------------------

  me(): Promise<any> { return this.get('/me'); }

  // ---- employees -----------------------------------------------------------

  employees(customerId?: string): Promise<any> { return this.get('/employees', { customerId }); }

  addEmployee(payload: any): Promise<any> { return this.post('/employees', payload); }

  async updateEmployee(membershipId: string, patch: any): Promise<any> {
    return this.http.patch(this.base + '/employees/' + membershipId,
      JSON.stringify(patch), await this.options()).toPromise();
  }

  setStatus(membershipId: string, status: boolean): Promise<any> {
    return this.post('/employees/' + membershipId + '/status', { status });
  }

  resetEmployeePassword(membershipId: string): Promise<any> {
    return this.post('/employees/' + membershipId + '/resetPassword', {});
  }

  ledger(membershipId: string): Promise<any> { return this.get('/employees/' + membershipId + '/ledger'); }

  async removeEmployee(membershipId: string, reason: string): Promise<any> {
    // DELETE with a body is awkward through HttpClient, so the reason rides as
    // a query param. It is only ever written to the membership's audit fields.
    return this.http.delete(this.base + '/employees/' + membershipId,
      await this.options({ reason })).toPromise();
  }

  // ---- meeting food (møtemat) ----------------------------------------------

  // `date` is optional: without it this returns just the locations and the
  // orderable dates, with it the menu for that day as well.
  cateringOptions(customerId?: string, date?: string): Promise<any> {
    return this.get('/catering/options', { customerId, date });
  }

  cateringOrders(customerId?: string): Promise<any> {
    return this.get('/catering/orders', { customerId });
  }

  placeCateringOrder(payload: any): Promise<any> {
    return this.post('/catering/orders', payload);
  }

  async updateCateringOrder(orderId: string, payload: any): Promise<any> {
    return this.http.patch(this.base + '/catering/orders/' + orderId,
      JSON.stringify(payload), await this.options()).toPromise();
  }

  async cancelCateringOrder(orderId: string, customerId?: string, reason?: string): Promise<any> {
    // Same reasoning as removeEmployee: a DELETE body is unreliable through
    // HttpClient, so both values ride as query params.
    return this.http.delete(this.base + '/catering/orders/' + orderId,
      await this.options({ customerId, reason })).toPromise();
  }

  // ---- reporting -----------------------------------------------------------

  purchases(from: number, to: number, opts?: { customerId?: string, groupDocId?: string }): Promise<any> {
    return this.get('/purchases', { from, to, ...(opts || {}) });
  }

  summary(from: number, to: number, customerId?: string): Promise<any> {
    return this.get('/summary', { from, to, customerId });
  }
}
